import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { injectIntl, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { colorCodeToColorName, tokenToHex } from '../helpers.ts';

import type {
	ContentStateGroupSetContentStateMutation as ContentStateGroupSetContentStateMutation$data,
	ContentStateGroupSetContentStateMutationVariables as ContentStateGroupSetContentStateMutation$variables,
} from './__types__/ContentStateGroupSetContentStateMutation.ts';
import EditorContentStateQuery from './__generated__/EditorContentStateQuery.graphql.ts';
import { ContentState } from './ContentState.tsx';
import { i18n } from './i18n.ts';
import type { ContentStateGroupProps, ContentState as ContentStateType } from './types.ts';
import { customGroupLabel } from './types.ts';
import { createNewCustomStatesArrayWhenExistingCustomSelected } from './helpers.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { ContentStateStateContainer } from './ContentStateStateContainer.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupLabel = styled.h3({
	marginBottom: token('space.150'),
	font: token('font.body'),
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateListWrapper = styled.ul({
	listStyleType: 'none',
	margin: token('space.0'),
	padding: token('space.0'),
});

export const ContentStateGroupComponent: FC<ContentStateGroupProps> = ({
	contentId,
	contentStateState,
	groupIsEnabled,
	groupLabel,
	contentStates,
	flags,
	userOffline,
	intl: { formatMessage },
	onUpdateSuccess,
	setIsContentStateMenuOpen,
}) => {
	// Need these refs to store original content states/array
	// in the case of an error (ex: user picks a state but somehow admin turned off at the same time) we need to
	// show the original values since we set it right after we mutate and there's no way to check if there are any
	// errors until after we mutate
	const originalContentState = useRef(contentStateState.state.contentState);
	const originalCustomStatesArray = useRef(contentStateState.state.customStatesArray);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(false);
	useEffect(() => {
		if (
			!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('cc_a4c_smart_button_changeboarding')
		) {
			setShouldSetAutomationFlag(true);
		}
	}, []);
	const isLivePage = useIsCurrentPageLive();
	const [setContentStateMutation] = useMutationInterop<
		ContentStateGroupSetContentStateMutation$data,
		ContentStateGroupSetContentStateMutation$variables
	>(
		graphql`
			mutation ContentStateGroupSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				// rollback to originals since they got updated and show error flag
				void contentStateState.setContentState(originalContentState.current);
				void contentStateState.setCustomStatesArray(originalCustomStatesArray.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: () => {
				// If a mutation was successfully completed, save the state/array in case an error occurs and we need to
				// rollback to these values
				originalContentState.current = contentStateState.state.contentState;
				originalCustomStatesArray.current = contentStateState.state.customStatesArray;
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			// Need to refetch since our cache isn't updating correctly - temporary solution until BE fixes
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const handleClick = useCallback(
		(newContentState: ContentStateType, label: string, index: number) => {
			void contentStateState.setIsUpdating(true);
			void setContentStateMutation({
				variables: {
					contentId,
					contentStateInput: {
						color: tokenToHex(newContentState.color),
						name: newContentState.name,
						id: newContentState.id as any, // TODO fix type
					},
				},
			});
			void contentStateState.setContentState(newContentState);
			setIsContentStateMenuOpen(false);
			// if label selected was a custom one, updated the custom list to add the selected label on top
			if (label === customGroupLabel) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					contentStateState.state.customStatesArray,
					newContentState,
					index,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: label, // "custom" or "suggested"
						color: colorCodeToColorName(newContentState.color!.toUpperCase()),
						restrictionLevel: newContentState.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			setContentStateMutation,
			setIsContentStateMenuOpen,
			isLivePage,
			shouldSetAutomationFlag,
		],
	);
	const contentStateList = contentStates.map((contentState, index) => {
		if (contentState) {
			return (
				<ContentState
					contentState={contentState}
					userOffline={userOffline}
					onClick={() => handleClick(contentState, groupLabel, index)}
					key={`${groupLabel}-content-state-${index}`}
				/>
			);
		}
	});
	return groupIsEnabled && contentStates.length > 0 ? (
		<div data-testid={`${groupLabel.toLowerCase()}-content-states`}>
			<GroupLabel>
				{groupLabel === customGroupLabel
					? formatMessage(i18n.custom)
					: formatMessage(i18n.suggested)}
			</GroupLabel>
			<ContentStateListWrapper aria-label={groupLabel}>{contentStateList}</ContentStateListWrapper>
		</div>
	) : null;
};

export const ContentStateGroup: FC<
	WithIntlProps<
		Omit<
			WrappedComponentProps & {
				contentId: string;
				contentStateState: ContentStateStateContainer;
				groupIsEnabled: boolean;
				groupLabel: string;
				contentStates: (ContentStateType | null)[];
				flags: FlagsStateContainer;
				userOffline: boolean;
				onUpdateSuccess?: () => void;
				setIsContentStateMenuOpen: (newValue: boolean) => void;
			},
			'flags'
		>
	>
> & {
	WrappedComponent: React.ComponentType<
		Omit<
			WrappedComponentProps & {
				contentId: string;
				contentStateState: ContentStateStateContainer;
				groupIsEnabled: boolean;
				groupLabel: string;
				contentStates: (ContentStateType | null)[];
				flags: FlagsStateContainer;
				userOffline: boolean;
				onUpdateSuccess?: () => void;
				setIsContentStateMenuOpen: (newValue: boolean) => void;
			},
			'flags'
		>
	>;
} = injectIntl(withFlags(ContentStateGroupComponent));
